import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import { UncontrolledDropdown, DropdownMenu, DropdownToggle, Card, Badge, DropdownItem, Alert, Spinner } from "reactstrap";
import {
  Button,
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  PaginationComponent,
} from "../../../components/Component";
import { Link, useLocation } from "react-router-dom";
import { API_BASE_URL, getPublicUrl } from "../../../utils/Utils";
import { isAbortError } from "../../../utils/asyncRequestGuards";
import { invoiceDisplayId, invoiceStatusBadgeColor, formatMoneyAmount } from "./invoiceHelpers";

const InvoiceList = () => {
  const location = useLocation();
  const medicalPartnerFilter = useMemo(() => {
    return new URLSearchParams(location.search).get("medicalPartner")?.trim() || "";
  }, [location.search]);

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [onSearch, setonSearch] = useState(true);
  const [onSearchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState("");
  const [totalDocs, setTotalDocs] = useState(0);
  const [refreshTick, setRefreshTick] = useState(0);
  const token = localStorage.getItem("accessToken");

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError("Not signed in");
      return undefined;
    }
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    axios
      .get(`${API_BASE_URL}/api/v1/invoices`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: currentPage,
          limit: itemPerPage,
          sort: "-createdAt",
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(medicalPartnerFilter ? { medicalPartner: medicalPartnerFilter } : {}),
        },
        signal: ac.signal,
      })
      .then((res) => {
        const rows = res.data.data || [];
        setInvoices(rows);
        setTotalDocs(typeof res.data.totalDocs === "number" ? res.data.totalDocs : rows.length);
      })
      .catch((err) => {
        if (!isAbortError(err)) {
          setInvoices([]);
          setTotalDocs(0);
          setError(err.response?.data?.message || err.message || "Failed to load invoices");
        }
      })
      .finally(() => setLoading(false));
    return () => ac.abort();
  }, [token, currentPage, itemPerPage, statusFilter, medicalPartnerFilter, refreshTick]);

  const filteredRows = useMemo(() => {
    if (!onSearchText.trim()) return invoices;
    const q = onSearchText.toLowerCase();
    return invoices.filter((item) => {
      const idLabel = invoiceDisplayId(item).toLowerCase();
      const kind = (item.kind || "").toLowerCase();
      return idLabel.includes(q) || kind.includes(q);
    });
  }, [invoices, onSearchText]);

  const onFilterChange = (e) => {
    setSearchText(e.target.value);
  };

  const toggle = () => setonSearch(!onSearch);

  const displayTotal = onSearchText.trim() ? filteredRows.length : totalDocs;

  return (
    <React.Fragment>
      <Head title="Invoice List"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Invoices</BlockTitle>
              <BlockDes className="text-soft">
                <p>
                  {loading
                    ? "Loading…"
                    : `Total ${displayTotal} invoice${displayTotal === 1 ? "" : "s"}${medicalPartnerFilter ? " (filtered by partner)" : ""}.`}
                </p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <ul className="nk-block-tools g-3">
                <li>
                  <Button
                    color="light"
                    className="btn-outline-light"
                    type="button"
                    onClick={() => setRefreshTick((t) => t + 1)}
                    disabled={loading}
                  >
                    <Icon name="reload"></Icon>
                    <span className="ms-1">Refresh</span>
                  </Button>
                </li>
              </ul>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        {error && (
          <Alert color="danger" className="mb-3">
            {error}
          </Alert>
        )}
        {medicalPartnerFilter && (
          <Alert color="info" className="py-2">
            Showing invoices for the selected medical partner.{" "}
            <Link to={getPublicUrl("invoice-list")}>Clear partner filter</Link>
          </Alert>
        )}

        <Block>
          <Card className="card-bordered card-stretch">
            <div className="card-inner-group">
              <div className="card-inner">
                <div className="card-title-group">
                  <div className="card-title">
                    <h5 className="title">All Invoices</h5>
                  </div>
                  <div className="card-tools mr-n1">
                    <div className="d-flex align-items-center flex-wrap gap-2">
                      <select
                        className="form-select form-select-sm"
                        style={{ width: "auto" }}
                        value={statusFilter}
                        onChange={(e) => {
                          setStatusFilter(e.target.value);
                          setCurrentPage(1);
                        }}
                        aria-label="Filter by status"
                      >
                        <option value="">All statuses</option>
                        <option value="draft">draft</option>
                        <option value="pending_issue">pending_issue</option>
                        <option value="issued">issued</option>
                        <option value="paid">paid</option>
                        <option value="void">void</option>
                        <option value="credited">credited</option>
                      </select>
                    </div>
                    <ul className="btn-toolbar">
                      <li>
                        <Button onClick={toggle} className="btn-icon search-toggle toggle-search">
                          <Icon name="search"></Icon>
                        </Button>
                      </li>
                      <li className="btn-toolbar-sep"></li>
                      <li>
                        <UncontrolledDropdown>
                          <DropdownToggle tag="a" className="dropdown-toggle btn btn-icon btn-trigger">
                            <Icon name="setting"></Icon>
                          </DropdownToggle>
                          <DropdownMenu right>
                            <ul className="link-check">
                              <li>
                                <span>Show</span>
                              </li>
                              <li className={itemPerPage === 10 ? "active" : ""}>
                                <DropdownItem
                                  tag="a"
                                  href="#dropdownitem"
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    setItemPerPage(10);
                                    setCurrentPage(1);
                                  }}
                                >
                                  10
                                </DropdownItem>
                              </li>
                              <li className={itemPerPage === 25 ? "active" : ""}>
                                <DropdownItem
                                  tag="a"
                                  href="#dropdownitem"
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    setItemPerPage(25);
                                    setCurrentPage(1);
                                  }}
                                >
                                  25
                                </DropdownItem>
                              </li>
                            </ul>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </li>
                    </ul>
                  </div>
                  <div className={`card-search search-wrap ${!onSearch ? "active" : ""}`}>
                    <div className="search-content">
                      <Button
                        className="search-back btn-icon toggle-search"
                        onClick={() => {
                          setSearchText("");
                          toggle();
                        }}
                      >
                        <Icon name="arrow-left"></Icon>
                      </Button>
                      <input
                        type="text"
                        className="form-control border-transparent form-focus-none"
                        placeholder="Search this page (number, kind)"
                        value={onSearchText}
                        onChange={onFilterChange}
                      />
                      <Button className="search-submit btn-icon">
                        <Icon name="search"></Icon>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-inner p-0">
                {loading ? (
                  <div className="p-4 text-center">
                    <Spinner color="primary" />
                  </div>
                ) : (
                  <table className="table table-orders">
                    <thead className="tb-odr-head">
                      <tr className="tb-odr-item">
                        <th className="tb-odr-info">
                          <span className="tb-odr-id">Invoice</span>
                          <span className="tb-odr-date d-none d-md-inline-block">Created</span>
                        </th>
                        <th className="tb-odr-amount">
                          <span className="tb-odr-total">Amount</span>
                          <span className="tb-odr-status d-none d-md-inline-block">Status</span>
                        </th>
                        <th className="tb-odr-action">&nbsp;</th>
                      </tr>
                    </thead>
                    <tbody className="tb-odr-body">
                      {filteredRows.length > 0
                        ? filteredRows.map((item) => {
                            const id = item._id;
                            const created = item.createdAt ? new Date(item.createdAt).toLocaleString("en-GB") : "—";
                            return (
                              <tr className="tb-odr-item" key={id}>
                                <td className="tb-odr-info">
                                  <span className="tb-odr-id">
                                    <Link to={getPublicUrl(`invoice-details/${id}`)}>#{invoiceDisplayId(item)}</Link>
                                  </span>
                                  <span className="tb-odr-date">{created}</span>
                                  <span className="d-block text-soft small">{item.kind || "—"}</span>
                                </td>
                                <td className="tb-odr-amount">
                                  <span className="tb-odr-total">
                                    <span className="amount">{formatMoneyAmount(item.totalAmount, item.currency)}</span>
                                  </span>
                                  <span className="tb-odr-status">
                                    <Badge color={invoiceStatusBadgeColor(item.status)} className="badge-dot text-capitalize">
                                      {item.status || "—"}
                                    </Badge>
                                  </span>
                                </td>
                                <td className="tb-odr-action">
                                  <div className="tb-odr-btns d-none d-sm-inline">
                                    <Link to={getPublicUrl(`invoice-print/${id}`)} target="_blank" rel="noopener noreferrer">
                                      <Button color="primary" size="sm" className="btn-icon btn-white btn-dim">
                                        <Icon name="printer-fill"></Icon>
                                      </Button>
                                    </Link>
                                    <Link to={getPublicUrl(`invoice-details/${id}`)}>
                                      <Button color="primary" size="sm" className="btn btn-dim">
                                        View
                                      </Button>
                                    </Link>
                                  </div>
                                  <Link to={getPublicUrl(`invoice-details/${id}`)}>
                                    <Button className="btn-pd-auto d-sm-none">
                                      <Icon name="chevron-right"></Icon>
                                    </Button>
                                  </Link>
                                </td>
                              </tr>
                            );
                          })
                        : null}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="card-inner">
                {!loading && filteredRows.length > 0 && !onSearchText.trim() ? (
                  <PaginationComponent
                    noDown
                    itemPerPage={itemPerPage}
                    totalItems={totalDocs}
                    paginate={(p) => setCurrentPage(p)}
                    currentPage={currentPage}
                  />
                ) : null}
                {!loading && filteredRows.length === 0 ? (
                  <div className="text-center">
                    <span className="text-silent">No invoices found</span>
                  </div>
                ) : null}
              </div>
            </div>
          </Card>
        </Block>
      </Content>
    </React.Fragment>
  );
};
export default InvoiceList;
